<template>
  <!-- Use NuxtImg for Cloudinary images -->
  <NuxtImg
    v-if="imageData.provider === 'cloudinary'"
    :provider="imageData.provider"
    :src="imageData.src"
    :alt="alt"
    :class="computedClass"
    :style="computedStyle"
    :sizes="sizes"
    :modifiers="modifiers"
    :width="width"
    :height="height"
  />
  <!-- Use regular img for local/other images -->
  <img
    v-else
    :src="imageData.src"
    :alt="alt"
    :class="computedClass"
    :style="computedStyle"
    :width="width"
    :height="height"
  />
</template>

<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { useImageCdn } from '~/composables/useImageCdn'

interface Props {
  src: string
  alt?: string
  imageClass?: string
  imageStyle?: string | Record<string, string>
  slug?: string
  sizes?: string
  modifiers?: Record<string, string | number>
  width?: string | number
  height?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  alt: '',
  imageClass: '',
  imageStyle: undefined,
  sizes: undefined,
  modifiers: undefined,
  width: undefined,
  height: undefined
})

const attrs = useAttrs()
const { resolveForDisplay } = useImageCdn()
const imageData = computed(() => resolveForDisplay(props.src, props.slug))

// Merge class from props (imageClass) and attrs (class)
const computedClass = computed(() => {
  const classFromAttrs = attrs.class as string | undefined
  const classFromProps = props.imageClass
  if (classFromAttrs && classFromProps) {
    return `${classFromAttrs} ${classFromProps}`
  }
  return classFromAttrs || classFromProps || ''
})

// Convert imageStyle prop to style (NuxtImg uses 'style', not 'imageStyle')
const computedStyle = computed(() => {
  if (typeof props.imageStyle === 'string') {
    return props.imageStyle
  }
  return props.imageStyle
})
</script>

